'use strict';

const i18n = require('i18next');
const Mousetrap = require('mousetrap');
const _ = require('underscore');
const Backbone = require('backbone');
const Mn = require('backbone.marionette');
const Util = require('common/util');
const Snackbar = require('common/util/snackbar');
const Clipboard = require('common/util/clipboard');
const ElementAction = require('system/domain/action/element');
const ElementRepository = require('system/domain/repository/element');
const ElementService = require('system/domain/service/element');
const PasteSymbolicModalTemplate = require('system/ui/template/modal/paste-symbolic.hbs');
const ModalView = require('common/ui/view/modal/index');
const CommitCommentModalView = require('system/ui/view/modal/commit-comment');
const ProjectRepository = require('common/domain/repository/project');

const PasteSymbolicModal = Mn.View.extend({
    template: PasteSymbolicModalTemplate,
    ui: {
        'addBtn': '.add',
        'cancelBtn': '.cancel',
        'toggleAll': '.toggle-all'
    },
    events: {
        'click @ui.addBtn': 'add',
        'click @ui.cancelBtn': 'cancel',
        'change @ui.toggleAll': 'toggleAll'
    },

    initialize: function(params) {
        this._symNodes = params.symNodes;
    },

    serializeData: function() {
        return {
            symbolicNodes: this._symNodes
        };
    },

    show: function() {
        this.$('div.modal').modal('show');
    },

    hide: function() {
        this.$('div.modal').modal('hide');
    },

    add: function() {
        const checked = this.$('.symbolic-node-candidates input:checked');
        const ids = _.map(checked, function(c) {
            return $(c).val();
        });
        this.trigger('add', ids);
        this.hide();
    },

    cancel: function() {
        this.hide();
    },

    toggleAll: function() {
        const checked = this.ui.toggleAll.is(':checked');
        const target = this.$('.symbolic-node-candidates input');
        if(checked) {
            target.prop('checked', true);
        } else {
            target.prop('checked', false);
        }
    }
});

module.exports = Backbone.Model.extend({
    initialize: function(params) {
        _.bindAll(this, 'copy', 'paste', 'delete', 'save', 'search',
            'onkeydown', 'onkeyup', 'fit', 'escape');
        this._diagram = null;
        this._isShiftPressed = false;
        this._context = params.context;
        this._channel = params.channel;
        this._auth = params.auth;
        this._diagram = null;
        this._nodes = ElementRepository.getNodes(
            {context: this._context});
        this._links = ElementRepository.getLinks(
            {context: this._context});

        this.listenTo(this._nodes, 'before:select', function() {
            if(this._isShiftPressed) {
                this._channel.request('change:mode', 'multi');
            }
        });
        this.listenTo(this._links, 'before:select', function() {
            if(this._isShiftPressed) {
                this._channel.request('change:mode', 'multi');
            }
        });
        document.onkeydown = this.onkeydown;
        document.onkeyup = this.onkeyup;

        Mousetrap.bind([
            'ctrl+c',
            'command+c'
        ], this.copy);

        Mousetrap.bind([
            'ctrl+v',
            'command+v',
            'ctrl+shift+v',
            'command+shift+v'
        ], this.paste);

        Mousetrap.bind([
            'ctrl+s',
            'command+s'
        ], this.save);

        Mousetrap.bind([
            'ctrl+f',
            'command+f'
        ], this.search);

        Mousetrap.bind([
            'ctrl+y',
            'command+y'
        ], this.fit);

        Mousetrap.bind([
            'del',
            'backspace'
        ], this.delete);

        Mousetrap.bind([
            'esc'
        ], this.escape);
    },

    setDiagram: function(d) {
        this._diagram = d;
    },

    getSelected: function(collection) {
        return collection.filter(function(model) {
            return model.selected;
        });
    },

    fit: function(e) {
        e.preventDefault();
        this._channel.request('canvas:showFull');
    },

    copy: function() {
        // If text is selected, do not copy element data
        if(window.getSelection().toString().length > 0) {
            return;
        }

        const center = this._channel.request('canvas:center');
        const n = _.map(this.getSelected(this._nodes), function(m) {
            const j = {id: m.id};
            const jModel = m.toJSON();
            delete jModel.id;
            j.model = jModel;
            const meta = this._diagram.getNodeMeta(m.id);
            if(meta) {
                const jMeta = meta.toJSON();
                delete jMeta.id;
                j.meta = jMeta;
                j.meta.x -= center.x;
                j.meta.y -= center.y;
            }
            return j;
        }, this);

        const l = _.map(this.getSelected(this._links), function(m) {
            const j = {id: m.id};
            const jModel = m.toJSON();
            delete jModel.id;
            j.model = jModel;
            const meta = this._diagram.getLinkMeta(m.id);
            if(meta) {
                const jMeta = meta.toJSON();
                delete jMeta.id;
                j.meta = jMeta;
            }
            return j;
        }, this);

        Clipboard.setText({
            text: JSON.stringify({
                type: 'BALUS',
                nodes: n,
                links: l
            }),
            onSet: function() {
                Snackbar.info(
                    i18n.t('view/diagram:shortcut.notify.copy')
                );
            }
        });
    },

    paste: function(e, combo) {
        if(!this._auth.isCreatable()) {
            return
        }
        Clipboard.getText({
            onGet: _.bind(this.onPaste, this, combo)
        });
    },

    onPaste: function(combo, text) {
        let j = {};
        try {
            j = JSON.parse(text);
        } catch(err) {
            Snackbar.info(
                i18n.t('view/diagram:shortcut.notify.invalidData')
            );
            return;
        }
        if(j.type !== 'BALUS') {
            Snackbar.info(
                i18n.t('view/diagram:shortcut.notify.invalidData')
            );
            return;
        }

        const center = this._channel.request('canvas:center');
        this._channel.trigger('cmd:selector:clear');
        const copy = combo.indexOf('shift') > 0;
        if(j.nodes.length > 1) {
            this._channel.request('change:mode', 'multi');
        }

        const add = _.bind(function(symNodeIds) {
            const idMap = {};
            _.each(j.nodes, function(n) {
                const model = n.model;
                const meta = n.meta;
                let id = null;

                if(copy || !_.contains(symNodeIds, n.id)) {
                    id = Util.generateId();
                    idMap[n.id] = id;
                    model.id = id;
                    meta.id = id;
                    meta.x = meta.x + 128 + center.x;
                    meta.y = meta.y + 128 + center.y;

                    ElementAction.createNode({
                        context: this._context,
                        node: model,
                        nodeMeta: meta
                    });
                } else {
                    id = n.id;
                    idMap[n.id] = id;
                    model.id = id;
                    meta.id = id;

                    ElementAction.createSymbolicNode({
                        context: this._context,
                        node: model,
                        nodeMeta: meta
                    });
                }

                ElementRepository.getNodes({
                    context: this._context
                }).get(id).select();
                ElementAction.startEditNode({
                    context: this._context,
                    node: {id: id}
                });
            }, this);

            _.each(j.links, function(l) {
                const id = Util.generateId();
                const model = l.model;
                const meta = l.meta;
                model.id = id;
                meta.id = id;
                model.source = idMap[model.source];
                model.target = idMap[model.target];
                if(!model.source || !model.target) {
                    return;
                }

                ElementAction.createLink({
                    context: this._context,
                    link: model,
                    linkMeta: meta
                });
                ElementRepository.getLinks({
                    context: this._context
                }).get(id).select();
                ElementAction.startEditLink({
                    context: this._context,
                    link: {id: id}
                });
            }, this);

            if(this._auth.isMultiSelectable()) {
                this._auth.trigger('change');
            }
        }, this);

        const symNodeCandidates = _.filter(j.nodes, function(n) {
            return !this._diagram.getNodeMeta(n.id);
        }, this);

        if(copy || symNodeCandidates.length === 0) {
            add();
        } else {
            const pasteSymbolic = new PasteSymbolicModal({
                symNodes: j.nodes
            });
            $('#modal-container').append(pasteSymbolic.render().$el);
            pasteSymbolic.show();
            $.material.init();
            pasteSymbolic.on('add', add);
        }
    },

    save: function(e) {
        e.preventDefault();

        if(!this._auth.isEditable()) {
            return
        }

        let selectedNodes = this.getSelected(this._nodes);
        let selectedNodeMetas = _.map(selectedNodes, function(m) {
            return this._diagram.getNodeMeta(m.id);
        }, this);
        let selectedLinks = this.getSelected(this._links);
        let selectedLinkMetas = _.map(selectedLinks, function(m) {
            return this._diagram.getLinkMeta(m.id);
        }, this);

        if(selectedNodes.length === 0 && selectedLinks.length === 0) {
            selectedNodes = this._nodes.filter(function(m) {
                return m.syncState.isUnsync();
            });
            selectedNodeMetas = this._diagram._nodeMetas.filter(function(m) {
                return m.syncState.isUnsync();
            });
            selectedLinks = this._links.filter(function(m) {
                return m.syncState.isUnsync();
            });
            selectedLinkMetas = this._diagram._linkMetas.filter(function(m) {
                return m.syncState.isUnsync();
            });
        }
        const nodes = _.filter(selectedNodes, function(m) {
            return m.syncState.isUnsync();
        });
        const nodeMetas = _.filter(selectedNodeMetas, function(m) {
            return m && m.syncState.isUnsync();
        });
        const links = _.filter(selectedLinks, function(m) {
            return m.syncState.isUnsync();
        });
        const linkMetas = _.filter(selectedLinkMetas, function(m) {
            return m && m.syncState.isUnsync();
        });

        if(nodes.length === 0 &&
            nodeMetas.length === 0 &&
            links.length === 0 &&
            linkMetas.length === 0) {
            return;
        }

        // this._context は oldContext なので、 projectId から Project を取得する
        ProjectRepository.getProject(this._context.projectId)
            .then(project => {
                const commentModalBody = new CommitCommentModalView({
                    context: this._context,
                    project: project,
                    diagram: this._diagram, // コミットコメントで関連付けるべきダイアグラムの情報を渡して、 Comment#relatedKeys に設定したい
                });

                const modal = new ModalView({
                    title: i18n.t('diagram:commitComment.title'),
                    childViewTriggers: {
                        'commit': 'commit'
                    },
                    bodyView: commentModalBody,
                    saveButtonEnable: true,
                    onChildviewSave: function() {
                        this.disableButtons();
                        commentModalBody.commit();
                        this.hide();
                    }
                });

                modal.on('commit', commitComment => {
                    this._channel.request('cmd:save', {
                        nodes: nodes,
                        links: links,
                        nodeMetas: nodeMetas,
                        linkMetas: linkMetas,
                        commitComment: commitComment,
                    });
                });

                modal.show();
            });
    },

    search: function(e) {
        e.preventDefault();
        this._channel.request('search', '');
    },

    delete: function() {
        _.each(this.getSelected(this._nodes), function(m) {
            m.deselect();
            const isMultiProjected = ElementService.getProjectedDiagrams({
                context: this._context,
                model: m
            }).length > 0;
            if(isMultiProjected) {
                ElementAction.deleteSymbolicNode({
                    context: this._context,
                    node: m.toJSON()
                });
            } else {
                ElementAction.deleteNode({
                    context: this._context,
                    node: m.toJSON()
                });
            }
        }, this);

        _.each(this.getSelected(this._links), function(m) {
            m.deselect();
            ElementAction.deleteLink({
                context: this._context,
                link: m.toJSON()
            });
        }, this);
    },

    escape: function() {
        this._channel.request('change:mode', 'edit');
    },

    onkeydown: function (e){
        if(e.keyCode === 16) {
            if(!this._isShiftPressed) {
                this._channel.trigger('keypress:shift');
            }
            this._isShiftPressed = true;
        }
    },

    onkeyup: function (e){
        if(e.keyCode === 16) {
            if(this._isShiftPressed) {
                this._channel.trigger('keyup:shift');
            }
            this._isShiftPressed = false;
        }
    }
});



// WEBPACK FOOTER //
// ./system/ui/view/shortcuts.js